import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { OAuthService } from '../oauth.service';
import { KeycloakService } from '../keycloak.service';

export type PlatformAccessMode = 'V1_ONLY' | 'V2_ONLY' | 'DUAL_ACCESS';

interface PlatformAccessResponse {
  platform_access_mode?: PlatformAccessMode;
}

/** Max time to wait for the access-mode response before failing closed (spec SC-004). */
const ACCESS_CHECK_TIMEOUT_MS = 5000;

/**
 * Platform-v1 (legacy) access gate. Calls Keycloak's platform-access-mode endpoint
 * once per session and caches the result. Allowed when mode is V1_ONLY or DUAL_ACCESS.
 * Fails closed on any error/timeout/unexpected shape. A failed check is NOT cached.
 */
@Injectable({ providedIn: 'root' })
export class PlatformAccessService {
  private accessPromise: Promise<boolean> | null = null;

  constructor(
    private http: HttpClient,
    private oauth: OAuthService,
    private keycloak: KeycloakService,
  ) {}

  /** True only when Keycloak is configured for this deployment. */
  isConfigured(): boolean {
    return this.keycloak.isEnabled();
  }

  /** Cached gate. Hits Keycloak once per session; a failure is not cached so a reload retries. */
  ensureAccessAllowed(): Promise<boolean> {
    if (!this.accessPromise) {
      this.accessPromise = this.checkOnce().catch((err) => {
        this.accessPromise = null; // FR-008: do not cache failures
        throw err;
      });
    }
    return this.accessPromise;
  }

  private async checkOnce(): Promise<boolean> {
    const { origin, realm } = environment.oauth;
    // Use a relative URL when running locally so the dev-server proxy forwards
    // the request to Keycloak — the custom SPI endpoint has no CORS headers,
    // so an absolute URL causes a CORS error in local dev.
    const base = window.location.hostname === 'localhost' ? window.location.origin : origin;
    const url = `${base}/realms/${realm}/platform-access-mode`;
    const token = this.oauth.accessToken || this.oauth.getAccessToken();
    const headers = new HttpHeaders({
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });

    try {
      const data = await firstValueFrom(
        this.http
          .get<PlatformAccessResponse>(url, { headers })
          .pipe(timeout(ACCESS_CHECK_TIMEOUT_MS)),
      );
      const mode = data?.platform_access_mode;
      return mode === 'V1_ONLY' || mode === 'DUAL_ACCESS';
    } catch (err) {
      // FR-019: log gate ERRORS only (policy denials return false above, unlogged)
      console.error('[PlatformAccessGate] access check failed:', err);
      throw err;
    }
  }
}
